/**
 * Background Removal API — all routes go through bgremove (REACT_APP_API_URL).
 */
import apiClient, { createAuthConfig, getApiBaseUrl, isMockApi } from "../client";
import {
  mockTestAuth,
  mockGetJobStatus,
  mockGetBatchStatus,
  mockGetCreations,
  mockGetCreationDownloadUrl,
  mockDeleteCreation,
  mockRemoveBackgroundSingle,
  mockFetchCreationImageBlob,
} from "../mocks";
import { appendLineageToForm } from "../tools/studio_features";
import { appendPreparedImage } from "../tools/aiUpload";

/**
 * Test authentication endpoint
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const testAuth = async (token) => {
  if (isMockApi) return mockTestAuth();
  const response = await apiClient.get(
    "/api/background-removal/test-auth",
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Remove background from single image via bgremove (returns creation_id for editor session).
 * @param {string} token - Clerk session token
 * @param {File} imageFile - Image file to process
 * @param {string} quality - Quality setting (default: 'high')
 * @param {string} format - Output format (default: 'png')
 * @param {Object} [lineage] - Optional parent/root creation ids (overlay Remove BG)
 * @returns {Promise<Object>} - { success, output_url, creation_id } or { job_id } for queue mode
 */
export const removeBackgroundSingle = async (
  token,
  imageFile,
  quality = "high",
  format = "png",
  lineage = null
) => {
  // Mock first: local passthrough (upload a pre-cut PNG to test editor without backend/AI).
  if (isMockApi) {
    return mockRemoveBackgroundSingle(imageFile);
  }

  const formData = new FormData();
  await appendPreparedImage(formData, "image", imageFile);
  formData.append("quality", quality);
  formData.append("format", format);
  appendLineageToForm(formData, lineage);

  const response = await apiClient.post(
    "/api/background-removal/single",
    formData,
    {
      ...createAuthConfig(token),
      timeout: 120000,
      headers: {
        ...createAuthConfig(token).headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

/**
 * Remove background from multiple images (batch processing via bgremove).
 */
export const removeBackgroundBatch = async (
  token,
  imageFiles,
  quality = "high",
  format = "png",
  maxImages = 10
) => {
  if (isMockApi) {
    return { success: true, results: imageFiles.slice(0, maxImages).map(() => ({ output_url: "" })) };
  }
  const formData = new FormData();
  for (const file of imageFiles) {
    await appendPreparedImage(formData, "images", file);
  }
  formData.append("quality", quality);
  formData.append("format", format);
  formData.append("max_images", maxImages.toString());

  const response = await apiClient.post(
    "/api/background-removal/batch",
    formData,
    {
      ...createAuthConfig(token),
      headers: {
        ...createAuthConfig(token).headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

/**
 * Get RQ job status for async background removal
 * @param {string} token - Clerk session token
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} - Job status
 */
export const getJobStatus = async (token, jobId) => {
  if (isMockApi) return mockGetJobStatus();
  const response = await apiClient.get(
    `/api/background-removal/job/${jobId}/status`,
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Get batch job status
 * @param {string} token - Clerk session token
 * @param {number} jobId - Batch job ID
 * @returns {Promise<Object>} - Batch job status
 */
export const getBatchStatus = async (token, jobId) => {
  if (isMockApi) return mockGetBatchStatus();
  const response = await apiClient.get(
    `/api/background-removal/batch/${jobId}/status`,
    createAuthConfig(token)
  );
  return response.data;
};

export const MY_DESIGNS_FILE_TYPES =
  "image,ai_background,upscale,outpaint,passport_photo";

/** Initial / incremental page size for My Designs gallery. */
export const MY_DESIGNS_PAGE_SIZE = 10;

/**
 * Get user's processed images (creations) and optional exports for My Designs.
 * @param {string} token
 * @param {{ skip?: number, limit?: number, fileTypes?: string, includeExports?: boolean }|number} [options]
 * @param {number} [legacyLimit] - backward compat when options is skip (number)
 */
export const getCreations = async (token, options = {}, legacyLimit) => {
  if (isMockApi) return mockGetCreations();
  // Backward compat: getCreations(token, skip, limit)
  if (typeof options === "number") {
    options = { skip: options, limit: legacyLimit ?? 50 };
  }
  const skip = options.skip ?? 0;
  const limit = options.limit ?? 50;
  const params = new URLSearchParams({
    skip: String(skip),
    limit: String(limit),
  });
  if (options.fileTypes) {
    params.set("file_types", options.fileTypes);
  }
  if (options.includeExports) {
    params.set("include_exports", "true");
  }
  const response = await apiClient.get(
    `/api/background-removal/creations?${params}`,
    {
      ...createAuthConfig(token),
      timeout: 60000,
    }
  );
  return response.data;
};

/** Map uploads API response to gallery items with stable selection ids. */
export function mapUploadItems(data) {
  return (data?.uploads || []).map((item) => ({
    ...item,
    id: `upload:${item.root_creation_id ?? item.id ?? item.creation_id}`,
    kind: "upload",
  }));
}

/**
 * Get user's original uploads (deduped by root_creation_id) for Uploads tab.
 */
export const getUploads = async (token, { skip = 0, limit = MY_DESIGNS_PAGE_SIZE } = {}) => {
  const params = new URLSearchParams({
    view: "uploads",
    skip: String(skip),
    limit: String(limit),
  });
  const response = await apiClient.get(
    `/api/background-removal/creations?${params}`,
    {
      ...createAuthConfig(token),
      timeout: 60000,
    }
  );
  return response.data;
};

/**
 * Persist an original image to the Uploads library (no BG removal).
 * @returns {Promise<Object>} upload item with root_creation_id, output_url, …
 */
export const registerLibraryUpload = async (token, imageFile) => {
  const formData = new FormData();
  formData.append("image", imageFile);
  const response = await apiClient.post(
    "/api/background-removal/uploads",
    formData,
    {
      ...createAuthConfig(token),
      timeout: 120000,
      headers: {
        ...createAuthConfig(token).headers,
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

/**
 * Return stored taxonomy label, or classify once from the original upload and persist.
 * @param {string} token
 * @param {number} creationId - root or any creation id in the upload tree
 * @returns {Promise<{ success: boolean, label?: string|null, persisted?: boolean }>}
 */
export const ensureCreationLabel = async (token, creationId) => {
  const response = await apiClient.post(
    `/api/background-removal/creations/${creationId}/ensure-label`,
    null,
    {
      ...createAuthConfig(token),
      timeout: 120000,
    }
  );
  return response.data;
};

export const fetchUploadImageBlob = async (token, rootCreationId) => {
  const response = await apiClient.get(
    `/api/background-removal/creations/${rootCreationId}/input-image`,
    {
      ...createAuthConfig(token),
      responseType: "blob",
      timeout: 120000,
    }
  );
  return response.data;
};

export const downloadUploadAttachment = async (token, rootCreationId) => {
  const response = await apiClient.get(
    `/api/background-removal/creations/${rootCreationId}/input-image?attachment=true`,
    {
      ...createAuthConfig(token),
      responseType: "blob",
      timeout: 120000,
    }
  );
  return response.data;
};

/** Merge creations + exports into flat design items sorted by created_at desc. */
export function mergeDesignItems(data) {
  const creations = (data?.creations || []).map((item) => ({
    ...item,
    id: `creation:${item.id ?? item.creation_id}`,
    kind: "creation",
  }));
  const exports = (data?.exports || []).map((item) => ({
    ...item,
    id: `export:${item.id ?? item.export_id}`,
    kind: "export",
  }));
  return [...creations, ...exports].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
}

export const getCreationDetail = async (token, creationId) => {
  const response = await apiClient.get(
    `/api/background-removal/creations/${creationId}`,
    createAuthConfig(token)
  );
  return response.data;
};

export const getExportDetail = async (token, exportId) => {
  const response = await apiClient.get(
    `/api/background-removal/exports/${exportId}`,
    createAuthConfig(token)
  );
  return response.data;
};

export const fetchExportImageBlob = async (token, exportId) => {
  const response = await apiClient.get(
    `/api/background-removal/exports/${exportId}/image`,
    {
      ...createAuthConfig(token),
      responseType: "blob",
      timeout: 120000,
    }
  );
  return response.data;
};

export const downloadCreationAttachment = async (token, creationId) => {
  const response = await apiClient.get(
    `/api/background-removal/creations/${creationId}/download?attachment=true`,
    {
      ...createAuthConfig(token),
      responseType: "blob",
      timeout: 120000,
    }
  );
  return response.data;
};

export const downloadExportAttachment = async (token, exportId) => {
  const response = await apiClient.get(
    `/api/background-removal/exports/${exportId}/download`,
    {
      ...createAuthConfig(token),
      responseType: "blob",
      timeout: 120000,
    }
  );
  return response.data;
};

export const deleteExport = async (token, exportId) => {
  const response = await apiClient.delete(
    `/api/background-removal/exports/${exportId}`,
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Get download URL for a creation
 * @param {string} token - Clerk session token
 * @param {number} creationId - Creation ID
 * @returns {Promise<Object>} - { success, download_url, expires_in }
 */
export const getCreationDownloadUrl = async (token, creationId) => {
  if (isMockApi) return mockGetCreationDownloadUrl();
  const response = await apiClient.get(
    `/api/background-removal/creations/${creationId}/download`,
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Stream creation image bytes via API (same-origin; used for canvas / editor restore).
 * Prefers processed output; on 404 falls back to original upload input
 * (library uploads often have no output yet).
 * @param {string} token - Clerk session token
 * @param {number} creationId - Creation ID
 * @returns {Promise<Blob>}
 */
export const fetchCreationImageBlob = async (token, creationId) => {
  if (isMockApi) {
    return mockFetchCreationImageBlob(token, creationId);
  }

  const url = `${getApiBaseUrl()}/api/background-removal/creations/${creationId}/image`;
  try {
    const response = await apiClient.get(
      `/api/background-removal/creations/${creationId}/image`,
      {
        ...createAuthConfig(token),
        responseType: "blob",
        timeout: 120000,
      }
    );
    return response.data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) {
      try {
        return await fetchUploadImageBlob(token, creationId);
      } catch {
        /* keep original error below */
      }
    }
    const hint =
      status === 404
        ? "No processed output or original upload found for this creation."
        : "Check REACT_APP_API_URL, HTTPS mixed content, and ALLOWED_ORIGINS on the API.";
    err.message = `${err.message || "Creation image fetch failed"} (${url}${status ? `, ${status}` : ""}). ${hint}`;
    throw err;
  }
};

/**
 * Delete a creation
 * @param {string} token - Clerk session token
 * @param {number} creationId - Creation ID
 * @returns {Promise<Object>}
 */
export const deleteCreation = async (token, creationId) => {
  if (isMockApi) return mockDeleteCreation();
  const response = await apiClient.delete(
    `/api/background-removal/creations/${creationId}`,
    createAuthConfig(token)
  );
  return response.data;
};

export default {
  testAuth,
  removeBackgroundSingle,
  removeBackgroundBatch,
  getJobStatus,
  getBatchStatus,
  getCreations,
  mergeDesignItems,
  MY_DESIGNS_FILE_TYPES,
  MY_DESIGNS_PAGE_SIZE,
  getUploads,
  mapUploadItems,
  registerLibraryUpload,
  ensureCreationLabel,
  fetchUploadImageBlob,
  downloadUploadAttachment,
  getCreationDetail,
  getExportDetail,
  getCreationDownloadUrl,
  fetchCreationImageBlob,
  fetchExportImageBlob,
  downloadCreationAttachment,
  downloadExportAttachment,
  deleteCreation,
  deleteExport,
};
